/**
 * PBC — the firm's requests to the client (client-visible tier). T6-H7:
 * create with an optional due date, control and detail; reply in the
 * thread; withdraw while Requested. T6-H14: the return reason is a labelled
 * field validated on submit. T6-H10: Accept / Return name the request.
 */
import { useState, type FormEvent } from 'react';
import { Alert, Card, EmptyState, Field, Input, Textarea } from '@aigovops/design-system';
import { firmApi, type PbcRequest } from '../../lib/firm-api';
import { fmtDate, plural } from '../../lib/format';
import { Chip, GuardedButton, StateBadge, TabHeading, useAct, useDraft, useFirm, useFocusLater, useLoad, Loading } from './firm-kit';

export function PbcTab() {
  const { slug, eng, writable, lockId } = useFirm();
  const l = useLoad(() => firmApi.pbc(slug, eng), [slug, eng]);
  const act = useAct(l.reload);
  const focus = useFocusLater();
  const [title, setTitle] = useDraft('pbc-title', '');
  const [control, setControl] = useDraft('pbc-control', '');
  const [due, setDue] = useDraft('pbc-due', '');
  const [detail, setDetail] = useDraft('pbc-detail', '');
  const [tried, setTried] = useState(false);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (title.trim().length < 3) return;
    if (await act.call('createPbc', [slug, eng, { title: title.trim(), control_ref: control.trim() || null, due_at: due || null, detail: detail.trim() || null }], `Request sent: ${title.trim()}`)) {
      setTitle(''); setControl(''); setDue(''); setDetail(''); setTried(false);
      focus('pbc-h');
    }
  };

  return (
    <div className="gp-firm-stack">
      <TabHeading id="pbc-h">Requests to the client</TabHeading>
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      <Loading l={l}>{(d) => d.requests.length === 0
        ? <EmptyState title="No requests yet" action={writable ? <a href="#pbc-new-title">Make the first request</a> : undefined} />
        : (
          <ul className="gp-firm-list" aria-labelledby="pbc-h">
            {d.requests.map((r) => <PbcRow key={r.request_id} r={r} onChanged={l.reload} />)}
          </ul>
        )}</Loading>
      {writable ? (
        <Card title="New request">
          <form className="gp-firm-form" onSubmit={create} noValidate>
            <Field id="pbc-new-title" label="What you need" error={tried && title.trim().length < 3 ? 'Describe the request (at least 3 characters).' : null}>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Q3 user access review export" />
            </Field>
            <div className="gp-firm-form--row">
              <Field id="pbc-new-control" label="Control (optional)"><Input value={control} onChange={(e) => setControl(e.target.value)} placeholder="CC6.2" /></Field>
              <Field id="pbc-new-due" label="Due (optional)"><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></Field>
            </div>
            <Field id="pbc-new-detail" label="Detail (optional)"><Textarea rows={2} value={detail} onChange={(e) => setDetail(e.target.value)} /></Field>
            <div className="gp-firm-actions"><GuardedButton type="submit" busy={act.busy}>Request from client</GuardedButton></div>
          </form>
        </Card>
      ) : <p className="gp-firm-muted" aria-describedby={lockId ?? undefined}>New requests are closed.</p>}
    </div>
  );
}

function PbcRow({ r, onChanged }: { r: PbcRequest; onChanged: () => void }) {
  const { slug, eng, writable } = useFirm();
  const act = useAct(onChanged);
  const focus = useFocusLater();
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useDraft(`pbc-return-${r.request_id}`, '');
  const [reply, setReply] = useDraft(`pbc-reply-${r.request_id}`, '');
  const [tried, setTried] = useState(false);
  const latest = r.versions[r.versions.length - 1];
  const rowId = `pbc-${r.request_id}`;

  const doReturn = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (reason.trim().length < 5) return;
    if (await act.call('returnPbc', [slug, eng, r.request_id, reason.trim()], `${r.ref} returned`)) { setReturning(false); setReason(''); setTried(false); focus(rowId); }
  };
  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!reply.trim()) { setTried(true); return; }
    if (await act.call('pbcMessage', [slug, eng, r.request_id, reply.trim()], `Message sent on ${r.ref}`)) { setReply(''); setTried(false); }
  };

  return (
    <li className="gp-firm-row" id={rowId} tabIndex={-1}>
      <div className="gp-firm-row-head">
        <h3 className="gp-firm-row-title">{r.ref} · {r.title}</h3>
        <StateBadge state={r.status} />
      </div>
      <p className="gp-firm-muted">
        {[r.control_ref, r.owner_name ? `Owner ${r.owner_name}` : 'No owner yet', r.due_at ? `Due ${fmtDate(r.due_at)}` : null, `Asked by ${r.requested_by_name}`].filter(Boolean).join(' · ')}
      </p>
      {r.detail ? <p className="gp-firm-muted">{r.detail}</p> : null}
      {act.error ? <Alert variant="error">{act.error}</Alert> : null}
      {latest ? (
        <>
          <p className="gp-firm-muted">
            v{latest.version} · {plural(latest.items.length, 'item')} · sent by {latest.submitted_by_name} {fmtDate(latest.submitted_at)}
            {latest.backfill_flag ? <> · <Chip tone="danger">Evidence created after the request</Chip></> : null}
          </p>
          <ul className="gp-firm-thread" aria-label={`Evidence in v${latest.version}`}>
            {latest.items.map((i) => <li key={i.ref_id}>{i.title} — created {fmtDate(i.source_created_at)}{i.backfilled ? ' (after the request)' : ''}</li>)}
          </ul>
        </>
      ) : <p className="gp-firm-muted">Waiting on the client</p>}
      {r.thread.length ? (
        <ul className="gp-firm-thread" aria-label={`Messages on ${r.ref}`}>
          {r.thread.map((m) => <li key={m.message_id}><strong>{m.author}</strong> · {fmtDate(m.posted_at)}: {m.body}</li>)}
        </ul>
      ) : null}
      {writable && r.status === 'submitted' ? (
        returning ? (
          <form className="gp-firm-form" onSubmit={doReturn} noValidate>
            <Field id={`ret-${r.request_id}`} label="Return reason" hint="The client sees this." error={tried && reason.trim().length < 5 ? 'Say what’s missing (at least 5 characters).' : null}>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
            <div className="gp-firm-actions">
              <GuardedButton type="submit" compact busy={act.busy}>Return {r.ref}</GuardedButton>
              <GuardedButton compact variant="ghost" onClick={() => { setReturning(false); setTried(false); }}>Cancel</GuardedButton>
            </div>
          </form>
        ) : (
          <div className="gp-firm-actions">
            <GuardedButton compact busy={act.busy} aria-label={`Accept ${r.ref}`} onClick={() => void act.call('acceptPbc', [slug, eng, r.request_id], `${r.ref} accepted`).then((ok) => ok && focus(rowId))}>Accept</GuardedButton>
            <GuardedButton compact aria-label={`Return ${r.ref}…`} onClick={() => { setReturning(true); focus(`ret-${r.request_id}`); }}>Return…</GuardedButton>
          </div>
        )
      ) : null}
      {writable && r.status === 'requested' ? (
        <div className="gp-firm-actions">
          <GuardedButton compact variant="ghost" busy={act.busy} aria-label={`Withdraw ${r.ref}`} onClick={() => void act.call('withdrawPbc', [slug, eng, r.request_id], `${r.ref} withdrawn`)}>Withdraw</GuardedButton>
        </div>
      ) : null}
      {r.status !== 'withdrawn' ? (
        <form className="gp-firm-form gp-firm-form--row" onSubmit={send} noValidate>
          <Field id={`reply-${r.request_id}`} label={`Reply on ${r.ref}`} error={tried && !reply.trim() ? 'Write a message first.' : null}>
            <Input value={reply} onChange={(e) => setReply(e.target.value)} />
          </Field>
          <div className="gp-firm-actions"><GuardedButton type="submit" compact busy={act.busy}>Send</GuardedButton></div>
        </form>
      ) : null}
    </li>
  );
}
